import React, { Component, ReactNode } from 'react';
import { CmsFilters } from 'js/model/cms/cms-browse-page';
import { channelContext } from 'js/components/channel';
import { ClearIcon, ClearIconColour } from 'js/components/controls/ClearIcon';
import { ChannelOutput } from 'js/model/rainbow/content/ChannelOutput';
import * as priceUtilities from 'js/helpers/price/priceUtilities';
import { createNonLinearSteps } from '../Filters/nonLinearSteps';
import { PriceRange } from './PriceRange';
import styles from './PriceRangeFilter.module.css';
import { findClosest } from '../Filters/price-range-filter';

const numberOfSteps = 20;
const endash = '\u2013';

interface Props {
  min: number;
  max: number;
  lower: number;
  upper: number;
  exponent: number;
  cms: CmsFilters;
  onChange: (lower: number, upper: number) => void;
  onAfterChange?: (lower: number, upper: number) => void;
}

interface State {
  lower: number;
  upper: number;
}

// Four steps is a reasonable minimum number of steps.
// The rc-slider <Range> component is configured to have a minimum of
// 2 steps between handles. So if a filter with only three steps were
// to be rendered the handles would not be draggable.
export function isPriceRangeFilterRenderableForRange(
  min: number,
  max: number,
  exponent: number
): boolean {
  const steps = createNonLinearSteps(min, max, numberOfSteps, exponent);
  return steps.length >= 4;
}

export class PriceRangeFilter extends Component<Props, State> {
  public state: State = {
    lower: this.props.lower,
    upper: this.props.upper,
  };

  private onChange = (lower: number, upper: number): void => {
    this.setState({ lower, upper });
    this.props.onChange(lower, upper);
  };

  private onAfterChange = (lower: number, upper: number): void => {
    this.setState({ lower, upper });
    this.props.onAfterChange?.(lower, upper);
  };

  public reset = (): void => {
    if (
      this.state.lower === this.props.min &&
      this.state.upper === this.props.max
    ) {
      // Nothing to change, so avoid doing anything that might result in
      // any tracking.
      return;
    }

    this.onChange(this.props.min, this.props.max);
    this.onAfterChange(this.props.min, this.props.max);
  };

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.lower !== this.props.lower ||
      prevProps.upper !== this.props.upper
    ) {
      this.setState({
        lower: this.props.lower,
        upper: this.props.upper,
      });
    }
  }

  public render(): ReactNode {
    return (
      <channelContext.Consumer>{this.renderForChannel}</channelContext.Consumer>
    );
  }

  private renderForChannel = (channel: ChannelOutput): ReactNode => {
    const anyPrice =
      this.state.lower === this.props.min &&
      this.state.upper === this.props.max;
    const availableSteps = createNonLinearSteps(
      this.props.min,
      this.props.max,
      numberOfSteps,
      this.props.exponent
    );
    const lowerBound = findClosest(availableSteps, this.state.lower);
    const upperBound = findClosest(availableSteps, this.state.upper);
    const lowerPrice = priceUtilities.formatPrice(lowerBound, channel);
    const upperPrice = priceUtilities.formatPrice(upperBound, channel);
    const selectedRange = `${lowerPrice} ${endash} ${upperPrice}`;

    return (
      <>
        <div className={styles.selectedRange}>
          {anyPrice ? (
            this.props.cms['any-price']
          ) : (
            <>
              {selectedRange}
              <div className={styles.reset} onClick={this.reset}>
                <ClearIcon colour={ClearIconColour.Blue} />
              </div>
            </>
          )}
        </div>

        <PriceRange
          steps={availableSteps}
          onChange={this.onChange}
          onAfterChange={this.onAfterChange}
          lower={lowerBound}
          upper={upperBound}
        />

        <div className={styles.minMaxValues}>
          <div>{priceUtilities.formatPrice(this.props.min, channel)}</div>
          <div>{priceUtilities.formatPrice(this.props.max, channel)}</div>
        </div>
      </>
    );
  };
}
